import React from 'react';
import { Modal, Form, InputNumber, Typography, Button } from 'antd';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';

const { Text } = Typography;

interface OpenCashRegisterModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (initialAmount: number) => void;
  loading: boolean;
}

const SUGGESTED_AMOUNTS = [0, 100, 200, 500, 1000];

/**
 * Abrir caja: pide UN dato, el fondo con el que arranca el turno.
 *
 * Antes el campo venía envuelto en cuatro bloques de texto (un aviso que
 * repetía el título, un "resumen" que repetía el número recién tecleado, y
 * tres advertencias de manual) que estiraban el modal a más del doble de su
 * alto y lo dejaban con scroll en teléfono. De esas advertencias solo una
 * tiene consecuencia real —si el fondo no coincide con el efectivo físico, el
 * arqueo sale descuadrado— y vive como pie del campo.
 *
 * Mismo lenguaje que AddCashMovementModal: monto grande en monoespaciada,
 * atajos como pills de un toque, sin cards anidadas.
 */
const OpenCashRegisterModal: React.FC<OpenCashRegisterModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const [form] = Form.useForm();
  // Del form, no de un useState paralelo: escribir a mano y tocar una pill
  // tienen que dejar la misma pill marcada.
  const initialAmount = Form.useWatch('initialAmount', form);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onConfirm(values.initialAmount);
    } catch {
      // validateFields ya pinta el error bajo el campo
    }
  };

  return (
    <Modal
      title="Abrir caja"
      open={visible}
      onCancel={onCancel}
      closable={!loading}
      maskClosable={!loading}
      keyboard={!loading}
      footer={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button type="text" onClick={onCancel} disabled={loading} style={{ marginRight: 'auto' }}>
            Cancelar
          </Button>
          <Button type="primary" loading={loading} disabled={loading} onClick={handleSubmit}>
            Abrir caja
          </Button>
        </div>
      }
      // Sin maxWidth en línea: pisarlo manda el modal fuera de pantalla en
      // tablets cuando hay `centered` (ver PR #175).
      width={isMobile ? '95%' : 520}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ initialAmount: 0 }}>
        <Form.Item
          name="initialAmount"
          label={<Text strong>Monto inicial</Text>}
          rules={[
            { required: true, message: 'Ingresa el monto inicial' },
            { type: 'number', min: 0, message: 'El monto no puede ser negativo' },
          ]}
          style={{ marginBottom: 10 }}
        >
          <InputNumber
            style={{
              width: '100%',
              fontFamily: 'ui-monospace, Consolas, monospace',
              fontSize: 20,
              fontWeight: 650,
            }}
            placeholder="0.00"
            min={0}
            precision={2}
            step={10}
            prefix={currencySymbol}
            size="large"
            autoFocus
          />
        </Form.Item>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {SUGGESTED_AMOUNTS.map((amount) => {
            const selected = initialAmount === amount;
            return (
              <button
                key={amount}
                type="button"
                onClick={() => form.setFieldsValue({ initialAmount: amount })}
                style={{
                  border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--border-color)'}`,
                  borderRadius: 999,
                  background: selected
                    ? 'color-mix(in srgb, var(--color-primary) 12%, var(--bg-primary))'
                    : 'var(--bg-primary)',
                  color: selected ? 'var(--color-primary)' : 'var(--text-secondary)',
                  padding: '5px 14px',
                  fontSize: 13,
                  fontWeight: selected ? 600 : 500,
                  fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                }}
              >
                {amount === 0 ? 'Sin fondo' : `${currencySymbol}${amount}`}
              </button>
            );
          })}
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Debe coincidir con el efectivo que dejas físicamente en la caja: es contra este monto que
          se calcula el corte.
        </Text>
      </Form>
    </Modal>
  );
};

export default OpenCashRegisterModal;
